/* ============================================================================
   ITG — Purchase Lock
   ============================================================================
   Loaded ONLY on pages that render a product locked from online purchase.

   Responsibilities:
     1. Open / close the "Request to Buy" popup, with focus management
     2. Keep the hidden Variant field in the request form in sync with the
        variant the customer is actually looking at
     3. Act as a defensive net: cancel any add-to-cart originating inside a
        [data-purchase-locked] container, and drop /cart/add requests for
        locked variant ids

   ISOLATION RULES (docs/TASK-contact-to-purchase-lock.md §3a, rule 5):
     * Every listener is scoped to [data-purchase-locked]. An unlocked
       product's quick-add button is never inspected.
     * The fetch patch is NOT installed when there are no locked variant ids
       on the page — fetch is left as native code, so normal cart, quick-add
       and section-render requests run completely untouched.
     * Blocking is by explicit variant-id set, never by URL pattern alone.

   No dependencies. Safe to load with defer.
   ========================================================================= */

(function () {
  "use strict";

  /* ── Locked variant ids present on this page ──────────────────────────── */

  function collectLockedVariantIds() {
    var ids = new Set();
    document.querySelectorAll("[data-purchase-locked][data-variant-ids]").forEach(
      function (el) {
        el.getAttribute("data-variant-ids")
          .split(",")
          .forEach(function (raw) {
            var id = raw.trim();
            if (id) ids.add(id);
          });
      },
    );
    return ids;
  }

  var lockedVariantIds = collectLockedVariantIds();

  window.ITGPurchaseLock = window.ITGPurchaseLock || {};
  window.ITGPurchaseLock.variants = lockedVariantIds;

  /* ── 1. Popup ─────────────────────────────────────────────────────────── */

  function initPopups() {
    document.querySelectorAll("[data-itg-plock]").forEach(function (root) {
      var uid = root.getAttribute("data-itg-plock");
      var openBtn = root.querySelector('[data-itg-plock-open="' + uid + '"]');
      var modal = root.querySelector("#ItgPlockModal-" + uid);

      if (!modal) return;

      var closeBtn = modal.querySelector('[data-itg-plock-close="' + uid + '"]');
      var lastFocused = null;

      function open() {
        lastFocused = document.activeElement;
        if (typeof modal.showModal === "function") {
          modal.showModal();
        } else {
          // <dialog> unsupported — fall back to a visible block so the form
          // is still reachable rather than silently unavailable.
          modal.setAttribute("open", "");
        }
        var firstField = modal.querySelector(
          "input:not([readonly]):not([type=hidden]), textarea",
        );
        if (firstField) firstField.focus();
      }

      function close() {
        if (typeof modal.close === "function" && modal.open) {
          modal.close();
        } else {
          modal.removeAttribute("open");
        }
        if (lastFocused && typeof lastFocused.focus === "function") {
          lastFocused.focus();
        }

        // If the customer dismissed the confirmation themselves, put the form
        // back so reopening does not show an empty popup. Guarded on the
        // confirmation being visible, so an ordinary close never discards
        // whatever they had already typed.
        var success = root.querySelector("[data-itg-plock-success]");
        if (success && !success.hidden) restoreForm(root);
      }

      if (openBtn) openBtn.addEventListener("click", open);
      if (closeBtn) closeBtn.addEventListener("click", close);

      // Click on the backdrop (the dialog element itself, outside its inner box)
      modal.addEventListener("click", function (event) {
        if (event.target === modal) close();
      });

      /*
       * CAPTCHA FIX — do not remove.
       *
       * Shopify's spam protection runs an invisible reCAPTCHA on submit. When it
       * decides to challenge, reCAPTCHA appends its puzzle overlay to <body>
       * with a very high z-index. But showModal() places this dialog in the
       * browser's TOP LAYER, which renders above ANY z-index - so the challenge
       * appeared behind the popup, could not be reached, and the enquiry was
       * never sent.
       *
       * We close the dialog ONLY once a challenge is actually on screen, rather
       * than on every submit. Closing unconditionally would leave the customer
       * staring at nothing whenever a submission failed without reloading the
       * page, which reads as "did that work?".
       *
       * Nothing here changes markup, styles or the submit itself. If no
       * challenge appears - the normal case - this does nothing at all and the
       * page navigates exactly as before.
       */
      var requestForm = modal.querySelector("form");
      if (requestForm) {
        requestForm.addEventListener("submit", function () {
          watchForCaptcha();
        });
      }

      /*
       * A visible captcha frame is a big one. The invisible reCAPTCHA keeps a
       * 0x0 anchor iframe and a 256x60 badge on the page at all times, so size
       * is what separates "present" from "actually challenging".
       */
      function captchaOnScreen() {
        var frames = document.querySelectorAll(
          'iframe[src*="recaptcha"], iframe[src*="hcaptcha"]',
        );
        for (var i = 0; i < frames.length; i++) {
          var rect = frames[i].getBoundingClientRect();
          if (rect.width > 150 && rect.height > 150) return true;
        }
        return false;
      }

      // Polls briefly after a submit, then gives up. Self-clearing, so there is
      // no lingering timer; on a successful submit the page navigates and it
      // dies with the page.
      function watchForCaptcha() {
        var attempts = 0;
        var timer = window.setInterval(function () {
          attempts += 1;

          if (attempts > 24 || !modal.open) {
            window.clearInterval(timer);
            return;
          }

          if (captchaOnScreen()) {
            window.clearInterval(timer);
            close();
          }
        }, 250);
      }

      // The form posts normally and Shopify returns to this page. Reopen the
      // popup so the customer sees the success or error state.
      if (root.hasAttribute("data-itg-plock-autoopen")) {
        open();
        scheduleSuccessDismiss(root, close);
      }
    });
  }

  /*
   * After a successful submission the popup shows only the confirmation, then
   * dismisses itself. Errors are left on screen so they can be read and fixed.
   *
   * Also strips ?contact_posted=true from the address bar, otherwise refreshing
   * or navigating back would pop the confirmation up again for a request that
   * was already sent.
   */
  function scheduleSuccessDismiss(root, closeFn) {
    var success = root.querySelector("[data-itg-plock-success]");
    if (!success) return;

    // Shopify appends this on a successful post; remove it so a reload is clean.
    try {
      var url = new URL(window.location.href);
      if (url.searchParams.has("contact_posted")) {
        url.searchParams.delete("contact_posted");
        window.history.replaceState({}, "", url.toString());
      }
    } catch (err) {
      // URL API unavailable — harmless, the popup still dismisses.
    }

    var seconds = parseInt(success.getAttribute("data-itg-plock-autohide"), 10);
    if (isNaN(seconds) || seconds <= 0) return; // 0 or blank means stay open

    window.setTimeout(function () {
      if (typeof closeFn === "function") closeFn();
      restoreForm(root);
    }, seconds * 1000);
  }

  /*
   * Swaps the confirmation back out for a fresh form.
   *
   * Without this, reopening the popup after a submission showed an empty box:
   * the confirmation had been hidden and, before the markup was changed, the
   * fields did not exist in the DOM at all. Reloading the page was the only way
   * to get the form back.
   */
  function restoreForm(root) {
    var success = root.querySelector("[data-itg-plock-success]");
    var fields = root.querySelector("[data-itg-plock-fields]");

    if (success) success.hidden = true;
    if (!fields) return;

    fields.hidden = false;

    // Clear what the customer typed so the next enquiry starts clean. The
    // product, variant and link inputs carry value attributes, so reset()
    // restores rather than blanks them.
    var formEl = fields.closest("form");
    if (formEl && typeof formEl.reset === "function") formEl.reset();
  }

  /* ── 2. Keep the hidden Variant field current ─────────────────────────── */

  function currentVariantLabel(root) {
    var productInfo = root.closest("product-info");
    var picker = productInfo && productInfo.querySelector("variant-selects");
    if (!picker) return null;

    var parts = [];
    var selects = picker.querySelectorAll("select");

    if (selects.length) {
      selects.forEach(function (select) {
        if (select.value) parts.push(select.value);
      });
    } else {
      picker
        .querySelectorAll('input[type="radio"]:checked')
        .forEach(function (input) {
          if (input.value) parts.push(input.value);
        });
    }

    if (!parts.length) return null;

    var label = parts.join(" / ");
    var variantId = new URLSearchParams(window.location.search).get("variant");
    if (variantId) label += " (ID: " + variantId + ")";

    return label;
  }

  function initVariantSync() {
    document.querySelectorAll("[data-itg-plock]").forEach(function (root) {
      var productInfo = root.closest("product-info");
      if (!productInfo) return;

      productInfo.addEventListener("change", function (event) {
        if (!event.target.closest("variant-selects")) return;

        // Let Dawn finish updating the URL before reading it.
        window.setTimeout(function () {
          var variantId = new URLSearchParams(window.location.search).get(
            "variant",
          );

          // Keep the product link pointing at the same variant we record,
          // so the enquiry email never names one variant and link another.
          root.querySelectorAll("[data-itg-plock-url]").forEach(function (field) {
            var base = field.getAttribute("data-itg-plock-url-base");
            if (!base) return;
            field.value = variantId ? base + "?variant=" + variantId : base;
          });

          var label = currentVariantLabel(root);
          if (!label) return;
          root
            .querySelectorAll("[data-itg-plock-variant]")
            .forEach(function (field) {
              field.value = label;
            });
        }, 0);
      });
    });
  }

  /* ── 3. Defensive net ─────────────────────────────────────────────────── */

  function blockLockedSubmits() {
    // Scoped to locked containers only — an unlocked card is never touched.
    document.addEventListener(
      "submit",
      function (event) {
        var form = event.target;
        if (!form || !form.closest) return;
        if (!form.closest("[data-purchase-locked]")) return;
        if (form.dataset.type !== "add-to-cart-form") return;

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true,
    );

    document.addEventListener(
      "click",
      function (event) {
        var btn = event.target.closest && event.target.closest('button[name="add"]');
        if (!btn) return;
        if (!btn.closest("[data-purchase-locked]")) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true,
    );
  }

  function extractVariantIds(body) {
    var ids = [];

    function pushId(value) {
      if (value === undefined || value === null) return;
      ids.push(String(value).trim());
    }

    try {
      if (typeof body === "string") {
        // Either JSON or urlencoded
        if (body.trim().charAt(0) === "{") {
          var parsed = JSON.parse(body);
          pushId(parsed.id);
          if (Array.isArray(parsed.items)) {
            parsed.items.forEach(function (item) {
              pushId(item.id);
            });
          }
        } else {
          new URLSearchParams(body).getAll("id").forEach(pushId);
        }
      } else if (body instanceof FormData || body instanceof URLSearchParams) {
        body.getAll("id").forEach(pushId);
      }
    } catch (err) {
      // Unparseable body — do not block. Fail open.
      return [];
    }

    return ids;
  }

  function installFetchGuard() {
    // Rule 5: with nothing to block, leave fetch completely alone.
    if (!lockedVariantIds.size) return;
    if (typeof window.fetch !== "function") return;

    var nativeFetch = window.fetch;

    window.fetch = function (input, init) {
      try {
        var url =
          typeof input === "string"
            ? input
            : input && input.url
              ? input.url
              : "";

        if (url.indexOf("/cart/add") !== -1) {
          var body = init && init.body ? init.body : null;
          var ids = extractVariantIds(body);
          var blocked = ids.some(function (id) {
            return lockedVariantIds.has(id);
          });

          if (blocked) {
            return Promise.reject(
              new Error(
                "ITG Purchase Lock: this product cannot be purchased online.",
              ),
            );
          }
        }
      } catch (err) {
        // Never let the guard break a legitimate request. Fail open.
      }

      return nativeFetch.apply(this, arguments);
    };
  }

  /* ── 4. Cart guard — remove locked lines ──────────────────────────────── */

  /*
   * Liquid has already blocked checkout server-side; this only clears the
   * offending lines so the customer is not left on a dead-end cart.
   *
   * No infinite-loop risk: on success the line is gone, so the notice does not
   * render on the reloaded page and this never runs again. On failure we do NOT
   * reload — the notice and the blocked checkout simply stay put.
   */
  function initCartGuard() {
    var notice = document.querySelector("[data-itg-plock-cart]");
    if (!notice) return;
    if (notice.getAttribute("data-itg-plock-autoremove") !== "true") return;

    var keys = (notice.getAttribute("data-itg-plock-cart-keys") || "")
      .split(",")
      .map(function (key) {
        return key.trim();
      })
      .filter(Boolean);

    if (!keys.length) return;

    // Guard against a redirect loop if the cart refuses to change.
    var attemptFlag = "itgPlockCartRemoval";
    try {
      if (window.sessionStorage.getItem(attemptFlag) === keys.join(",")) return;
      window.sessionStorage.setItem(attemptFlag, keys.join(","));
    } catch (err) {
      // sessionStorage unavailable (private mode) — proceed without the guard.
    }

    // Use the theme's own route (set in theme.liquid) so this keeps working
    // under a locale or market subpath.
    var changeUrl =
      (window.routes && window.routes.cart_change_url) || "/cart/change";

    // Sequential, not parallel: /cart/change.js reindexes lines on each call,
    // so concurrent requests would remove the wrong ones.
    keys
      .reduce(function (chain, key) {
        return chain.then(function () {
          return fetch(changeUrl + ".js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: key, quantity: 0 }),
          });
        });
      }, Promise.resolve())
      .then(function () {
        window.location.reload();
      })
      .catch(function () {
        // Leave the notice and the blocked checkout in place.
      });
  }

  /* ── Boot ─────────────────────────────────────────────────────────────── */

  function init() {
    initPopups();
    initVariantSync();
    blockLockedSubmits();
    installFetchGuard();
    initCartGuard();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-scan after Shopify section re-renders (theme editor, variant changes).
  document.addEventListener("shopify:section:load", function () {
    lockedVariantIds = collectLockedVariantIds();
    window.ITGPurchaseLock.variants = lockedVariantIds;
    initPopups();
    initVariantSync();
  });
})();
